import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, LogOut, Shield } from 'lucide-react';
import { supabase } from '../utils/supabaseStorage';
import { useAppStore } from '../store/useAppStore';
import { isStaffRole } from '../types/roles';
import { GetStartedAuthModal } from '../components/GetStartedAuthModal';
import { SubscriptionPlanGrid, type SubscriptionPlanRow } from '../components/billing/SubscriptionPlanGrid';
import { CreditPackGrid, type CreditPackRow } from '../components/billing/CreditPackGrid';
import { startCreditPackCheckout, startSubscriptionCheckout } from '../utils/billingCheckout';

export function ProfilePage() {
  const navigate = useNavigate();
  const session = useAppStore((s) => s.session);
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const userRole = useAppStore((s) => s.userRole);
  const logout = useAppStore((s) => s.logout);
  const canSeeAdmin = isStaffRole(userRole);
  const [email, setEmail] = useState<string | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [getStartedOpen, setGetStartedOpen] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlanRow[]>([]);
  const [hasPaid, setHasPaid] = useState(false);
  const [nextFree, setNextFree] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<
    { id: string; amount_paise: number; status: string; paid_at: string | null; short_url: string | null; receipt_url: string | null }[]
  >([]);
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);
  const [busyPackId, setBusyPackId] = useState<string | null>(null);
  const [billingErr, setBillingErr] = useState<string | null>(null);
  const [packErr, setPackErr] = useState<string | null>(null);
  const [creditPacks, setCreditPacks] = useState<CreditPackRow[]>([]);
  const [paidSub, setPaidSub] = useState<{
    plan_id?: string;
    plan_name?: string;
    credits_per_period?: number;
    current_period_end?: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      setEmail(user?.email ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!session?.hy_user_id) {
      setCredits(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: u, error } = await supabase
        .from('users')
        .select('credit_balance')
        .eq('hy_user_id', session.hy_user_id)
        .maybeSingle();
      if (cancelled) return;
      if (!error && u && typeof (u as { credit_balance?: number }).credit_balance === 'number') {
        setCredits((u as { credit_balance: number }).credit_balance);
      } else {
        setCredits(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.hy_user_id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      const { data: p } = await supabase
        .from('subscription_plans')
        .select('id, code, name, amount_paise, credits_per_period, is_paid, is_active')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (cancelled) return;
      setPlans((p as SubscriptionPlanRow[]) || []);

      const { data: cp } = await supabase
        .from('credit_pack_products')
        .select('id, code, name, credits, amount_paise')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (cancelled) return;
      setCreditPacks((cp as CreditPackRow[]) || []);

      const { data: pr } = await supabase.rpc('get_my_billing_profile');
      if (cancelled) return;
      if (pr && typeof pr === 'object') {
        const o = pr as {
          has_paid_subscription?: boolean;
          next_free_credit_reset_at?: string | null;
          paid_subscription?: { plan_name?: string; credits_per_period?: number; current_period_end?: string | null } | null;
        };
        setHasPaid(Boolean(o.has_paid_subscription));
        setNextFree(o.next_free_credit_reset_at ? String(o.next_free_credit_reset_at) : null);
        const ps = o.paid_subscription && typeof o.paid_subscription === 'object' ? o.paid_subscription : null;
        setPaidSub(
          ps
            ? {
                plan_id: typeof (ps as { plan_id?: string }).plan_id === 'string' ? (ps as { plan_id: string }).plan_id : undefined,
                plan_name: (ps as { plan_name?: string }).plan_name,
                credits_per_period: (ps as { credits_per_period?: number }).credits_per_period,
                current_period_end: (ps as { current_period_end?: string | null }).current_period_end ?? null,
              }
            : null
        );
      }

      const { data: inv } = await supabase
        .from('billing_invoices')
        .select('id, amount_paise, status, paid_at, short_url, receipt_url')
        .order('created_at', { ascending: false })
        .limit(20);
      if (cancelled) return;
      setInvoices(Array.isArray(inv) ? (inv as typeof invoices) : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleSubscribe = async (planId: string) => {
    setBillingErr(null);
    setBusyPlanId(planId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const result = await startSubscriptionCheckout(planId, {
        name: 'Hyper subscription',
        email: user?.email ?? undefined,
      });
      if (result.planChangedWithoutCheckout) {
        const msg =
          result.scheduleChangeAt === 'cycle_end'
            ? 'Your plan change is scheduled for the end of this billing period.'
            : 'Your subscription plan has been updated.';
        window.alert(msg);
        const { data: pr } = await supabase.rpc('get_my_billing_profile');
        if (pr && typeof pr === 'object') {
          const o = pr as {
            has_paid_subscription?: boolean;
            paid_subscription?: Record<string, unknown> | null;
          };
          setHasPaid(Boolean(o.has_paid_subscription));
          const ps = o.paid_subscription && typeof o.paid_subscription === 'object' ? o.paid_subscription : null;
          setPaidSub(
            ps
              ? {
                  plan_id: typeof ps.plan_id === 'string' ? ps.plan_id : undefined,
                  plan_name: typeof ps.plan_name === 'string' ? ps.plan_name : undefined,
                  credits_per_period: typeof ps.credits_per_period === 'number' ? ps.credits_per_period : undefined,
                  current_period_end: (ps.current_period_end as string | null | undefined) ?? null,
                }
              : null
          );
        }
      }
    } catch (e) {
      setBillingErr(e instanceof Error ? e.message : 'Checkout failed');
    } finally {
      setBusyPlanId(null);
    }
  };

  const handleBuyPack = async (packId: string) => {
    setPackErr(null);
    setBusyPackId(packId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await startCreditPackCheckout(packId, { email: user?.email ?? undefined });
      const hyId = session?.hy_user_id;
      if (hyId) {
        const { data: u } = await supabase.from('users').select('credit_balance').eq('hy_user_id', hyId).maybeSingle();
        if (u && typeof (u as { credit_balance?: number }).credit_balance === 'number') {
          setCredits((u as { credit_balance: number }).credit_balance);
        }
      }
    } catch (e) {
      setPackErr(e instanceof Error ? e.message : 'Checkout failed');
    } finally {
      setBusyPackId(null);
    }
  };

  return (
    <div className="min-h-0 w-full text-th-primary">
      <GetStartedAuthModal
        open={getStartedOpen}
        onClose={() => setGetStartedOpen(false)}
        onSignedIn={() => {
          setGetStartedOpen(false);
          void navigate('/library', { replace: true });
        }}
      />
      <div className="border-b border-subtle bg-surface/80">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <h1 className="text-2xl font-bold">Profile</h1>
          {credits !== null && (
            <div className="w-fit rounded-full border border-subtle bg-elevated px-4 py-1.5 text-sm">
              <span className="text-th-secondary">Credits</span>{' '}
              <span className="font-semibold tabular-nums text-th-primary">{credits}</span>
            </div>
          )}
        </div>
      </div>
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        {isAuthenticated && session ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-subtle bg-elevated p-5">
              <p className="text-sm text-th-secondary">Signed in as</p>
              <p className="mt-1 font-medium text-th-primary">
                {session.hy_name?.trim() || email || 'Member'}
              </p>
              {email ? <p className="mt-0.5 text-sm text-th-tertiary break-all">{email}</p> : null}
            </div>
            <div className="rounded-2xl border border-subtle bg-surface/60 p-5 text-left">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-th-tertiary">Billing & plans</h2>
              {hasPaid && paidSub ? (
                <p className="mt-2 text-sm text-th-secondary">
                  Current plan: <span className="font-medium text-th-primary">{paidSub.plan_name}</span> —{' '}
                  {paidSub.credits_per_period?.toLocaleString() ?? '—'} credits / month.{' '}
                  {paidSub.current_period_end
                    ? `Renews or period ends: ${new Date(paidSub.current_period_end).toLocaleString()}.`
                    : null}
                </p>
              ) : (
                <p className="mt-2 text-sm text-th-secondary">
                  You are on the <span className="font-medium text-th-primary">Free</span> plan (100 credits / month, no rollover).{' '}
                  {nextFree ? `Next free reset: ${new Date(nextFree).toLocaleDateString()}.` : null}
                </p>
              )}
              <div className="mt-4">
                <SubscriptionPlanGrid
                  plans={plans}
                  hasPaidSubscription={hasPaid}
                  currentPlanId={paidSub?.plan_id ?? null}
                  busyPlanId={busyPlanId}
                  error={billingErr}
                  onSubscribe={handleSubscribe}
                />
                {creditPacks.length > 0 ? (
                  <div className="mt-6 border-t border-subtle pt-6">
                    <h3 className="text-sm font-semibold text-th-primary">Buy credits (one-time)</h3>
                    <div className="mt-3">
                      <CreditPackGrid
                        packs={creditPacks}
                        busyPackId={busyPackId}
                        error={packErr}
                        onBuy={handleBuyPack}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {invoices.length > 0 ? (
              <div className="rounded-2xl border border-subtle bg-elevated p-5 text-left">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-th-tertiary">Invoices</h2>
                <ul className="mt-3 space-y-2 text-sm">
                  {invoices.map((iv) => (
                    <li key={iv.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-subtle/60 py-2 last:border-0">
                      <span className="text-th-primary">
                        ₹{(iv.amount_paise / 100).toLocaleString('en-IN')} — {iv.status}
                      </span>
                      <span className="text-th-tertiary">
                        {iv.paid_at ? new Date(iv.paid_at).toLocaleString() : '—'}
                      </span>
                      {iv.receipt_url || iv.short_url ? (
                        <a
                          href={iv.receipt_url || iv.short_url || '#'}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-brand hover:underline"
                        >
                          Receipt <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Link
                to="/library"
                className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2.5 text-center text-sm font-medium text-surface hover:bg-brand-hover"
              >
                Your library
              </Link>
              {canSeeAdmin && (
                <Link
                  to="/admin"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-subtle bg-surface px-4 py-2.5 text-sm text-th-primary hover:bg-overlay"
                >
                  <Shield className="h-4 w-4" aria-hidden />
                  Admin
                </Link>
              )}
              <button
                type="button"
                onClick={() => void logout()}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-subtle px-4 py-2.5 text-sm text-th-secondary hover:border-status-error/40 hover:text-status-error"
              >
                <LogOut className="h-4 w-4" />
                Log out
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 rounded-2xl border border-subtle bg-elevated/80 p-6">
            <p className="text-th-secondary">Sign in to use your library, manage subscriptions, and chat with your agents.</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => setGetStartedOpen(true)}
                className="rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-surface hover:bg-brand-hover"
              >
                Get started
              </button>
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-lg border border-subtle px-4 py-2.5 text-sm font-medium text-th-primary hover:bg-overlay"
              >
                Sign in
              </Link>
            </div>
            <p className="text-xs text-th-tertiary">New to Hyper App Store? “Get started” helps you create an account.</p>
          </div>
        )}
        <p className="text-xs text-th-tertiary">
          <Link to="/" className="text-brand hover:underline">
            Back to the store
          </Link>
        </p>
      </div>
    </div>
  );
}
